import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Search, Sparkles, EyeOff } from "lucide-react";
import { z } from "zod";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ArticleCard } from "@/components/ArticleCard";
import { getAllDiscoveries, type DBCategory, type DiscoveryListItem } from "@/lib/content.functions";
import { getMyProfile } from "@/lib/profile.functions";
import { ARCHETYPES, type ArchetypeId } from "@/lib/archetypes";
import { resolveImage } from "@/lib/asset-map";
import { supabase } from "@/integrations/supabase/client";
import { getHistory } from "@/lib/history";
import { SeasonFolders } from "@/components/seasons/SeasonFolders";
import { groupIntoSeriesFolders } from "@/lib/seasons";
import { useLocale } from "@/hooks/use-locale";
import { countCopy, listCopy } from "@/lib/list-locales";

export const ARCHETYPE_IDS = ["explorer", "skeptic", "dreamer", "tinkerer", "storyteller"] as const;

const searchSchema = z.object({
  category: z.string().optional(),
  sort: z.enum(["newest", "oldest", "popular", "trending", "random"]).optional(),
  q: z.string().optional(),
  archetype: z.enum(ARCHETYPE_IDS).optional(),
  filter: z.enum(["from-reels"]).optional(),
});

export const Route = createFileRoute("/discoveries")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ category: search.category, sort: search.sort ?? "newest", q: search.q, archetype: search.archetype, filter: search.filter }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["discoveries-hub", deps.category ?? null, deps.sort, deps.q ?? null, deps.archetype ?? null, deps.filter ?? null],
      queryFn: () =>
        getAllDiscoveries({
          data: { categorySlug: deps.category, sort: deps.sort, q: deps.q, archetype: deps.archetype, filter: deps.filter },
        }),
    }),

  head: ({ loaderData }) => {
    const cat = loaderData?.activeCategory;
    const title = cat ? `${cat.label} Discoveries · Smartasaurus` : "All Discoveries · Smartasaurus";
    const desc = cat?.blurb ?? "Browse every Smartasaurus discovery - weird animals, oceans, space, dangerous nature, the human body and unexplained phenomena.";
    const url = "https://smartasaurus.com/discoveries";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: DiscoveriesPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center">
        <div>
          <p className="text-muted-foreground">Couldn't load discoveries.</p>
          <button onClick={() => { router.invalidate(); reset(); }} className="mt-4 rounded-full bg-primary px-4 py-2 text-sm font-bold uppercase text-primary-foreground">Retry</button>
        </div>
      </div>
    );
  },
});

function DiscoveriesPage() {
  const data = Route.useLoaderData() as { categories: DBCategory[]; items: DiscoveryListItem[]; activeCategory: DBCategory | null };
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const sort = search.sort ?? "newest";
  const { categories, items, activeCategory } = data;
  const [lang] = useLocale();
  const copy = listCopy(lang);
  const counts = countCopy(lang);
  const catLabel = (id: string | null) =>
    categories.find((c) => c.id === id)?.label ?? "Discovery";

  const [qInput, setQInput] = useState(search.q ?? "");
  useEffect(() => setQInput(search.q ?? ""), [search.q]);
  useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = qInput.trim();
      if ((search.q ?? "") === trimmed) return;
      navigate({
        search: (prev: z.infer<typeof searchSchema>) => ({ ...prev, q: trimmed || undefined }),
        replace: true,
      });
    }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Load user's archetype once (if signed in) to power the "For you" toggle.
  const fetchProfile = useServerFn(getMyProfile);
  const [myArchetype, setMyArchetype] = useState<ArchetypeId | null>(null);
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data: s }) => {
      if (!s.session) return;
      fetchProfile()
        .then((p) => {
          if (cancelled) return;
          const a = p?.archetype as ArchetypeId | null | undefined;
          if (a && ARCHETYPE_IDS.includes(a)) setMyArchetype(a);
        })
        .catch(() => {});
    });
    return () => { cancelled = true; };
  }, [fetchProfile]);

  const activeArchetype: ArchetypeId | null = search.archetype ?? null;
  const archetypeMeta = activeArchetype ? ARCHETYPES[activeArchetype] : null;

  // Unread filter - hides discoveries already opened by this visitor.
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [readSlugs, setReadSlugs] = useState<Set<string>>(new Set());
  useEffect(() => {
    setReadSlugs(new Set(getHistory().map((h) => h.slug)));
  }, []);
  const visibleItems = useMemo(
    () => (unreadOnly ? items.filter((d) => !readSlugs.has(d.slug)) : items),
    [items, unreadOnly, readSlugs],
  );

  // Folder view: themed folders → seasons → numbered episodes. Off while searching.
  const [folderView, setFolderView] = useState(true);
  const useFolders = folderView && !search.q && visibleItems.length > 8;


  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-24">
         <div data-no-translate className="border-b border-border pb-6">
           <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· {copy.archive}</div>
          <h1 className="mt-2 font-display text-4xl uppercase leading-none md:text-6xl">
             {activeCategory ? activeCategory.title : copy.articles}
          </h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">
             {activeCategory?.blurb ?? copy.archiveBlurb}
          </p>
        </div>

         <div data-no-translate className="mt-6 relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
             placeholder={copy.search}
            className="w-full rounded-full border border-border bg-card py-3 pl-11 pr-4 text-base placeholder:text-muted-foreground focus:border-primary focus:outline-none"
            style={{ fontSize: "16px" }}
            inputMode="search"
            autoComplete="off"
            autoCorrect="off"
          />
        </div>

         <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 rounded-2xl border border-border bg-card px-4 py-2.5">
             <span data-no-translate className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{copy.category}</span>
            <select
              value={activeCategory?.slug ?? ""}
              onChange={(e) => {
                const slug = e.target.value || undefined;
                navigate({ search: (prev: z.infer<typeof searchSchema>) => ({ ...prev, category: slug }), replace: false });
              }}
              className="min-w-0 bg-transparent text-sm font-semibold text-foreground focus:outline-none"
              style={{ fontSize: "16px" }}
            >
               <option data-no-translate value="">{copy.allCategories}</option>
              {categories.map((c) => (
                <option key={c.id} value={c.slug}>{c.label}</option>
              ))}
            </select>
          </label>

          <label className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-3 rounded-2xl border border-border bg-card px-4 py-2.5">
             <span data-no-translate className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{copy.sort}</span>
            <select
              value={sort}
              onChange={(e) => {
                const v = e.target.value as "newest" | "oldest" | "popular" | "trending" | "random";
                navigate({ search: (prev: z.infer<typeof searchSchema>) => ({ ...prev, sort: v }), replace: true });
              }}
              className="min-w-0 bg-transparent text-sm font-semibold text-foreground focus:outline-none"
              style={{ fontSize: "16px" }}
            >
               <option data-no-translate value="newest">{copy.newest}</option>
               <option data-no-translate value="popular">{copy.popular}</option>
               <option data-no-translate value="trending">{copy.trending}</option>
               <option data-no-translate value="oldest">{copy.oldest}</option>
               <option data-no-translate value="random">{copy.random}</option>
            </select>
          </label>
        </div>

        {(myArchetype || activeArchetype) && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {myArchetype && (
              <button
                onClick={() => {
                  navigate({
                    search: (prev: z.infer<typeof searchSchema>) => ({
                      ...prev,
                      archetype: activeArchetype ? undefined : myArchetype,
                    }),
                    replace: true,
                  });
                }}
                className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-[11px] font-bold uppercase tracking-widest transition ${
                  activeArchetype
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-primary/40 bg-primary/5 text-primary hover:bg-primary/10"
                }`}
              >
                <Sparkles className="h-3.5 w-3.5" />
                 {activeArchetype ? copy.forYouOn : copy.forYou}
              </button>
            )}
            {archetypeMeta && (
              <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                {archetypeMeta.emoji} Tuned for {archetypeMeta.name}
              </span>
            )}
          </div>
        )}

         <div data-no-translate className="mt-4 flex flex-wrap items-center justify-between gap-3 text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/collections" className="rounded-full border border-border px-3 py-1 hover:border-primary hover:text-primary">
               {copy.series}
            </Link>
            {readSlugs.size > 0 && (
              <button
                type="button"
                onClick={() => setUnreadOnly((v) => !v)}
                aria-pressed={unreadOnly}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 transition ${
                  unreadOnly
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border hover:border-primary hover:text-primary"
                }`}
              >
                 <EyeOff className="h-3 w-3" /> {copy.unreadOnly}
              </button>
            )}
            {!search.q && visibleItems.length > 8 && (
              <button
                type="button"
                onClick={() => setFolderView((v) => !v)}
                aria-pressed={folderView}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 transition ${
                  folderView
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border hover:border-primary hover:text-primary"
                }`}
              >
                 {folderView ? copy.topicsView : copy.gridView}
              </button>
            )}
          </div>
           <span>{counts.showing(visibleItems.length)}</span>
        </div>

        {visibleItems.length === 0 ? (
          <p className="mt-10 text-muted-foreground">
            {unreadOnly ? "You've opened everything that matches - nice work." : "No discoveries match your filters."}
          </p>
        ) : useFolders ? (
          <SeasonFolders
            folders={groupIntoSeriesFolders(visibleItems, categories, { seasonSize: 10 })}
          />
        ) : (
           <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
            {visibleItems.map((d, i) => (
              <ArticleCard
                key={d.id}
                slug={d.slug}
                img={resolveImage(d.image_url)}
                tag={catLabel(d.category_id)}
                title={d.title}
                excerpt={d.excerpt}
                views={d.views_label ?? undefined}
                size={i === 0 && !activeCategory && !search.q ? "lg" : "md"}
              />
            ))}
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
